import React, { useEffect, useMemo, useRef } from "react";
import {
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { copyText } from "../utils/clipboard";
import { buildNodeUri } from "../quiltUri";
import { LOCATION_EDGE_TYPE_LABEL, isLocationEdgeType } from "../locationTypes";
import { hasGoogleMapsKey, loadGoogleMaps } from "../utils/googlePlaces";

function buildGoogleMapsUrl(place) {
  const placeId = place?.props?.placeId || "";
  const lat = Number(place?.props?.lat);
  const lng = Number(place?.props?.lng);
  if (placeId) return `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(placeId)}`;
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  return "https://www.google.com/maps";
}

function PlaceMapPreview({ place }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const lat = Number(place?.props?.lat);
  const lng = Number(place?.props?.lng);

  useEffect(() => {
    let active = true;
    const renderMap = async () => {
      if (!containerRef.current || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const maps = await loadGoogleMaps();
      if (!active || !maps) return;
      const position = { lat, lng };
      if (!mapRef.current) {
        mapRef.current = new maps.Map(containerRef.current, {
          center: position,
          zoom: 14,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
      }
      if (!markerRef.current) {
        markerRef.current = new maps.Marker({ map: mapRef.current, position });
      } else {
        markerRef.current.setPosition(position);
      }
      mapRef.current.setCenter(position);
    };
    renderMap();
    return () => {
      active = false;
    };
  }, [lat, lng]);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return <Typography variant="caption" color="text.secondary">No coordinates available.</Typography>;
  }

  if (!hasGoogleMapsKey()) {
    return <Typography variant="caption" color="text.secondary">Map preview unavailable (missing Google Maps key).</Typography>;
  }

  return <Paper ref={containerRef} sx={{ height: 180, width: "100%", borderRadius: 1.5 }} />;
}

export default function PlaceDetailsSection({ selectedNode, nodes, edges, onFocusNode }) {
  const incomingLocationEdges = useMemo(() => {
    if (!selectedNode) return [];
    return edges.filter((edge) => edge.to?.nodeId === selectedNode.id && isLocationEdgeType(edge.type));
  }, [selectedNode, edges]);

  const nodeById = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);

  const summary = useMemo(() => {
    const counts = new Map();
    incomingLocationEdges.forEach((edge) => {
      const next = Number(counts.get(edge.type) || 0) + 1;
      counts.set(edge.type, next);
    });
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [incomingLocationEdges]);

  if (!selectedNode) return null;

  const mapsUrl = buildGoogleMapsUrl(selectedNode);
  const placeName = selectedNode.props?.name || selectedNode.label || "Unnamed place";
  const formattedAddress = selectedNode.props?.formattedAddress || "No formatted address";

  return (
    <>
      <Divider />
      <Typography variant="subtitle2" fontWeight={700}>Place Details</Typography>
      <Stack spacing={0.6}>
        <Typography variant="body2"><b>Name:</b> {placeName}</Typography>
        <Typography variant="body2"><b>Address:</b> {formattedAddress}</Typography>
        <Typography variant="body2"><b>Lat/Lng:</b> {selectedNode.props?.lat ?? "-"}, {selectedNode.props?.lng ?? "-"}</Typography>
        <Typography variant="body2"><b>Place ID:</b> {selectedNode.props?.placeId || "-"}</Typography>
        <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
          {(selectedNode.props?.googleTypes || []).map((item) => (
            <Chip key={item} size="small" label={item} />
          ))}
        </Stack>
      </Stack>

      <PlaceMapPreview place={selectedNode} />

      <Stack direction="row" spacing={1}>
        <Button size="small" onClick={() => void copyText(buildNodeUri(selectedNode.id))}>Copy Quilt Link</Button>
        <Button
          size="small"
          onClick={() => window.open(mapsUrl, "_blank", "noopener,noreferrer")}
        >
          Open in Google Maps
        </Button>
      </Stack>

      <Typography variant="subtitle2" fontWeight={700}>Connected to this Place</Typography>
      <Typography variant="caption" color="text.secondary">
        {summary.length ? summary.map(([type, count]) => `${count} ${LOCATION_EDGE_TYPE_LABEL[type] || type}`).join(", ") : "No incoming location edges."}
      </Typography>
      <Stack spacing={0.8}>
        {incomingLocationEdges.map((edge) => {
          const subject = nodeById.get(edge.from?.nodeId);
          return (
            <Stack key={edge.id} direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Chip size="small" label={LOCATION_EDGE_TYPE_LABEL[edge.type] || edge.type} />
              <Button size="small" onClick={() => onFocusNode(subject?.id)}>
                {subject?.label || subject?.id || edge.from?.nodeId}
              </Button>
            </Stack>
          );
        })}
      </Stack>
    </>
  );
}
